import React, { useState } from 'react';
import { Box, Button, CustomDropZone, ErrorStateInfo, Grid, Icon, Typography } from 'web-library';
import { IconName } from 'web-library/stories/atom/Icon/Icon';
import IconWithLabel from 'web-library/stories/atom/IconWithLabel/IconWithLabel';
import { TFunction, useTranslation } from 'react-i18next';

import { makeStyles } from 'tss-react/mui';
import { formatBytes } from '../../utils/FileUtils';
import { FileProcessingState, FileUploadState } from '../../models/File';

import { typeTheme } from 'web-library/theme/helper/tssHelper';
const useStyles = makeStyles<{ amendment: boolean }>()((theme, { amendment }) => ({
  description: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(4),
  },
  uploadErrorSection: {
    '& img': {
      width: 45,
      height: 45,
    },
  },
  uploadErrorDescription: {
    ...typeTheme(theme, 'body1bold'),
  },
  dropZone: {
    marginBottom: theme.spacing(2),
    width: '100%',
  },
  amendmentDropZone: {
    marginBottom: theme.spacing(2),
    width: '100%',
    '& .MuiDropzoneArea-text': {
      color: amendment ? theme.color.text.textDisabled : '',
    },
    borderColor: amendment ? theme.color.border.borderFocused : '',
    pointerEvents: amendment ? 'none' : 'auto',
    caretColor: 'transparent',
  },
  dropZoneHelperTextContainer: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: theme.spacing(4),
  },
  dropZoneHelperText: {
    color: theme.color.text.textPrimary,
  },
  attachedFilesHeader: {
    color: theme.color.text.textHeader,
    marginBottom: theme.spacing(2),
  },
  fileBadgeParentContainer: {
    height: theme.spacing(4),
    marginBottom: theme.spacing(4),
  },
  fileBadgeContainer: {
    height: theme.spacing(4),
    flexWrap: 'nowrap',
  },
  iconContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'start',
    justifySelf: 'start',
    marginRight: theme.spacing(2),
  },
  documentIcon: {
    color: theme.color.text.textTertiary,
  },
  filename: {
    width: theme.spacing(60),
    textOverflow: 'ellipsis',
    flexGrow: 2,
    fontWeight: 'bold',
    color: theme.color.link.linkDefault01,
  },
  fileSize: {
    width: theme.spacing(20),
    justifySelf: 'start',
    color: theme.color.text.textTertiary,
    paddingRight: theme.spacing(5),
    [theme.breakpoints.down('sm')]: {
      paddingRight: theme.spacing(2),
    },
  },
  removeCell: {
    width: theme.spacing(12),
  },
  removeButton: {
    padding: 0,
    height: theme.spacing(4),
    color: theme.color.icon.iconTrash,
  },
}));

interface ExcelUploadProps {
  onUploadButtonClicked: () => void;
  uploadAndProcessStatus: FileUploadState | FileProcessingState;
  uploadedFiles: File[];
  setUploadedFiles: React.Dispatch<React.SetStateAction<File[]>>;
  amendment?: boolean;
}

interface GetInstructionProps {
  uploadAndProcessStatus: FileUploadState | FileProcessingState;
  classes: Record<string, string>;
  t: TFunction<'translation'>;
}

const getInstruction = ({ uploadAndProcessStatus, classes, t }: GetInstructionProps) =>
  uploadAndProcessStatus === FileUploadState.FAILED && (
    <Box className={classes.uploadErrorSection}>
      <Typography variant="body1" className={classes.description}>
        {t('components->>ExcelUpload.UploadErrorInstruction')}
      </Typography>
      <ErrorStateInfo>
        <Typography variant="body1" className={classes.uploadErrorDescription}>
          {t('components->>ExcelUpload.UploadErrorDescription')}
        </Typography>
      </ErrorStateInfo>
    </Box>
  );

const ExcelUpload = ({
  onUploadButtonClicked,
  uploadedFiles,
  setUploadedFiles,
  uploadAndProcessStatus,
  amendment = false,
}: ExcelUploadProps) => {
  const { classes } = useStyles({ amendment });
  const { t } = useTranslation();
  const [dropzoneKey, setDropzoneKey] = useState<string>(Date.now().toString());

  const maxFileSize = 314572800;
  const acceptedFileTypes = ['.xlsx'];

  const onFileDelete = () => {
    setUploadedFiles([]);
    setDropzoneKey(Date.now().toString());
  };

  return (
    <>
      {getInstruction({ uploadAndProcessStatus, classes, t })}
      <Grid xs={12} sm={12} md={12} lg={12}>
        <CustomDropZone
          filesLimit={1}
          acceptedFiles={acceptedFileTypes}
          maxSize={maxFileSize}
          key={dropzoneKey}
          className={amendment ? classes.amendmentDropZone : classes.dropZone}
          onChange={setUploadedFiles}
          initialFiles={uploadedFiles}
          onDelete={onFileDelete}
        />
      </Grid>
      <Box className={classes.dropZoneHelperTextContainer}>
        <Typography className={classes.dropZoneHelperText}>
          <u>
            <b>{t('components->>ExcelUpload.FileUploadRequirement')}</b>
          </u>
        </Typography>
        <ul>
          <li>
            <Typography className={classes.dropZoneHelperText}>
              {t('components->>ExcelUpload.AcceptedFileTypes', {
                acceptedFileTypes: '.xlsx',
              })}
            </Typography>
          </li>
          <li>
            <Typography className={classes.dropZoneHelperText}>
              {t('components->>ExcelUpload.AcceptedFileSize', {
                acceptedFileNum: '1',
                acceptedFileSize: '300MB',
              })}
            </Typography>
          </li>
          <li>
            <Typography className={classes.dropZoneHelperText}>
              {t('components->>ExcelUpload.FileUploadNotAccept1')}
            </Typography>
          </li>
        </ul>
      </Box>
      {uploadedFiles.length > 0 && (
        <>
          <Typography variant="h5" className={classes.attachedFilesHeader}>
            {t('components->>ExcelUpload.AttachedFiles')}
          </Typography>
          <Box className={classes.fileBadgeParentContainer}>
            <Grid
              container
              direction="row"
              alignItems="center"
              justify="flex-start"
              wrap="nowrap"
              className={classes.fileBadgeContainer}
            >
              <Box className={classes.iconContainer}>
                <Icon size="M" name={IconName.document} className={classes.documentIcon} />
              </Box>
              <Typography noWrap variant="h5" className={classes.filename}>
                {uploadedFiles[0].name}
              </Typography>
              <Grid container justify="flex-end" className={classes.fileSize}>
                <Typography noWrap variant="body1">
                  {formatBytes(uploadedFiles[0].size)}
                </Typography>
              </Grid>
              <Grid container className={classes.removeCell}>
                <Button
                  className={classes.removeButton}
                  variant="text"
                  onClick={() => onFileDelete()}
                  disabled={amendment}
                  id="button-remove-file"
                >
                  <IconWithLabel
                    iconName={IconName.trash}
                    iconSize="M"
                    label={t('components->>ExcelUpload.Remove')}
                    typographyProps={{
                      variant: 'h5',
                      color: 'inherit',
                    }}
                  />
                </Button>
              </Grid>
            </Grid>
          </Box>
        </>
      )}
      <Button
        id="button-upload-file"
        disabled={uploadedFiles.length === 0}
        onClick={onUploadButtonClicked}
      >
        <IconWithLabel
          iconName={IconName.export}
          iconSize="M"
          label={t('components->>ExcelUpload.UploadFile')}
          typographyProps={{
            variant: 'h5',
            color: 'inherit',
          }}
        />
      </Button>
    </>
  );
};

export default ExcelUpload;
